'use client'

// "Fokus Minggu Ini" — ringkas per dev: due minggu ini + yang telat. Sekali lihat.
import { AlertTriangle } from 'lucide-react'
import type { Task, Sprint } from '@/lib/types'
import { taskInSprint, weekOfSprint, isoDay } from '@/lib/dev-sprint'
import type { BoardData, DevDerived } from './types'
import { Avatar, PointsChip } from './ui'

const norm = (x?: string | null) => (x ?? '').trim().toLowerCase()

interface Props {
  board: BoardData
  sprint: Sprint
  derived: DevDerived
  today: string
  onOpenTask: (id: string) => void
}

export function WeekFocusStrip({ board, sprint, derived, today, onOpenTask }: Props) {
  const { statusOf } = derived
  const curWeekRaw = weekOfSprint(today, sprint)
  const active = curWeekRaw != null // hari ini ada di dalam sprint?
  const curWeek = curWeekRaw ?? 1

  const perDev = board.team
    .filter((m) => m.username)
    .map((m) => {
      const key = norm(m.username)
      const mine = board.tasks.filter((t) => norm(t.assignee) === key && statusOf(t) !== 'done')
      const dueThisWeek = active ? mine.filter((t) => taskInSprint(t, sprint) && weekOfSprint(t.dueDate, sprint) === curWeek) : []
      const late = mine.filter((t) => { const d = isoDay(t.dueDate); return d && d < today })
      // Task paling mendesak: telat dulu, lalu due minggu ini
      const urgent = [...late, ...dueThisWeek.filter((t) => !late.includes(t))].slice(0, 2)
      return { member: m, dueCount: dueThisWeek.length, lateCount: late.length, urgent }
    })

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-[13px] font-bold text-[#1A1916]">Fokus Minggu Ini</h3>
        <span className="text-[11px] text-[#AEADA8]">{active ? `Week ${curWeek}` : 'Sprint tidak sedang berjalan'}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {perDev.map(({ member, dueCount, lateCount, urgent }) => (
          <div key={member.email} className="rounded-2xl border border-[#E7E5DF] bg-white p-3">
            <div className="flex items-center gap-2 mb-2.5">
              <Avatar name={member.name} size={28} />
              <span className="text-[13px] font-semibold text-[#1A1916] truncate flex-1">{member.name}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-[#F0EEE7] text-[#6B6A65] tabular-nums" title="Due minggu ini">{dueCount} due</span>
                {lateCount > 0 && (
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-[#FCE8E8] text-[#C0362C] tabular-nums inline-flex items-center gap-1" title="Telat">
                    <AlertTriangle className="w-3 h-3" />{lateCount}
                  </span>
                )}
              </div>
            </div>
            {urgent.length === 0 ? (
              <p className="text-[12px] text-[#AEADA8]">Tidak ada yang mendesak 👍</p>
            ) : (
              <ul className="space-y-1">
                {urgent.map((t) => {
                  const p = derived.projById.get(t.projectId)
                  const late = (() => { const d = isoDay(t.dueDate); return d && d < today })()
                  return (
                    <li key={t.id}>
                      <button onClick={() => onOpenTask(t.id)} className="w-full flex items-center gap-1.5 text-left rounded-lg px-1.5 py-1 hover:bg-[#FAF9F6] transition-colors">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: p?.color ?? '#D4A843' }} />
                        <span className="text-[12px] text-[#4A4844] truncate flex-1">{t.title}</span>
                        <PointsChip points={t.points} />
                        {late && <span className="text-[10px] font-bold text-[#C0362C]">TELAT</span>}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
